import { useMemo } from 'react';
import type { JSX } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';
import type { Company, Site } from '@/data/schemas';
import { queryKeys } from '@/lib/query-keys';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import { useModalMutation } from '../../../../hooks/useModalMutation';
import {
  personnelFormFields,
  personnelFormSchema,
  type PersonnelFormValues,
} from '../../components/personnelFormSchema';
import { formatSiteSequence, formatSiteShorthand } from '@/utils/entityIdentity';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { useDataProvider } from '@/data/provider';

interface NewPersonnelModalProps {
  companyId: string;
  companyCode?: string | null;
  sites: Array<{ id: string; name: string; sequenceNumber?: number }>;
  defaultScope?: 'company' | 'site';
  defaultSiteId?: string;
  lockScope?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewPersonnelModal({
  companyId,
  companyCode,
  sites,
  defaultScope,
  defaultSiteId,
  lockScope = false,
  open,
  onOpenChange,
}: NewPersonnelModalProps): JSX.Element {
  const { contacts } = useDataProvider();
  const companyStub: Pick<Company, 'code'> | null = useMemo(
    () => (companyCode ? { code: companyCode } : null),
    [companyCode]
  );
  const siteOptions = useMemo(
    () =>
      sites.map((site) => {
        const siteFragment: Pick<Site, 'sequenceNumber'> = {
          sequenceNumber: site.sequenceNumber ?? 0,
        };
        const shorthand = formatSiteShorthand(companyStub, siteFragment);
        const sequence = formatSiteSequence(site.sequenceNumber);
        return {
          value: site.id,
          label: shorthand
            ? `${site.name} (${shorthand})`
            : sequence
              ? `${site.name} (${sequence})`
              : site.name,
        };
      }),
    [companyStub, sites]
  );

  const resolvedScope = defaultScope ?? 'company';

  const { fields } = useUnifiedForm({
    schema: personnelFormSchema,
    fields: personnelFormFields,
    fieldOrder: [
      'firstName',
      'lastName',
      'role',
      'scope',
      'siteId',
      'email',
      'phone',
      'reportRecipient',
      'alarmContact',
      'reperibile',
      'fireTraining',
      'firstAidTraining',
      'licensed',
      'licenseExpiry',
      'licenseLastUpdate',
    ],
    fieldOverrides: {
      scope: {
        options: [
          { value: 'company', label: 'Azienda intera' },
          { value: 'site', label: 'Stabilimento specifico' },
        ],
        disabled: lockScope,
      },
      siteId: {
        options: siteOptions,
        disabled: (values: Record<string, unknown>) =>
          lockScope || values.scope !== 'site' || siteOptions.length === 0,
        dependsOn: ['scope'],
      },
      licenseExpiry: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
      licenseLastUpdate: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
    },
  });

  const createPersonnel = useModalMutation<PersonnelFormValues, void>({
    mutationFn: async (values) => {
      await contacts.add(companyId, {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        role: values.role,
        scope: values.scope,
        siteId: values.scope === 'site' ? (values.siteId ?? undefined) : undefined,
        reportRecipient: values.reportRecipient,
        alarmContact: values.alarmContact,
        fireTraining: values.fireTraining,
        firstAidTraining: values.firstAidTraining,
        licensed: values.licensed,
        licenseExpiry: values.licenseExpiry,
        licenseLastUpdate: values.licenseLastUpdate,
        reperibile: values.reperibile,
      });
    },
    successMessage: 'Personale aggiunto',
    errorMessage: 'Impossibile aggiungere il personale',
    invalidate: [queryKeys.companies.all, queryKeys.companies.detail(companyId)],
    onSuccess: () => {
      onOpenChange(false);
    },
    onError: () => true,
  });

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: personnelFormSchema,
      fields,
      data: null,
      createDefaults: {
        scope: resolvedScope,
        siteId: defaultSiteId ?? undefined,
      },
    });

  const handleSubmit = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      await createPersonnel.mutateAsync(values as PersonnelFormValues);
    },
    setRootError,
  });

  const isSaving = createPersonnel.isPending;
  const isDirty = form.formState.isDirty;

  const sections = [
    {
      title: 'Anagrafica',
      fields: ['firstName', 'lastName', 'role', 'email', 'phone'],
    },
    {
      title: 'Assegnazioni',
      fields: ['scope', 'siteId'],
    },
    {
      title: 'Abilitazioni',
      fields: [
        'reportRecipient',
        'alarmContact',
        'reperibile',
        'fireTraining',
        'firstAidTraining',
        'licensed',
        'licenseExpiry',
        'licenseLastUpdate',
      ],
    },
  ];

  return (
    <EntityDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Nuovo personale"
      size="sm"
      isDirty={isDirty}
      isSaving={isSaving}
      formId={formId}
      banner={rootError ? <FormErrorBanner message={rootError} /> : null}
      summary={
        errorCount > 0 ? (
          <FormErrorSummary
            count={errorCount}
            onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
          />
        ) : null
      }
      footer={
        <EntityDialogFooter
          secondary={
            <DialogClose asChild>
              <Button variant="ghost" disabled={isSaving}>
                Chiudi
              </Button>
            </DialogClose>
          }
          primary={
            <Button type="submit" form={formId} disabled={!isDirty || isSaving}>
              {isSaving ? 'Salvataggio…' : 'Crea personale'}
            </Button>
          }
        />
      }
    >
      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
          noValidate
          className="space-y-4"
        >
          <EntityFormFields fields={fields} sections={sections} disabled={isSaving} />
        </form>
      </FormProvider>
    </EntityDialog>
  );
}
